// Small-print field labels on a Malaysian vehicle ownership certificate
// (geran). The large title survives a blurry photo, so it is left out: only
// a photo sharp enough to verify shows these to the OCR.
const GERAN_LABELS = [
  "PENDAFTARAN",
  "CHASIS",
  "ENJIN",
  "PEMUNYA",
  "ALAMAT",
  "BUATAN",
  "KEUPAYAAN",
  "BAHAN BAKAR",
  "STATUS ASAL",
  "KEGUNAAN",
  "BADAN",
  "TARIKH",
  "SYARAT",
];

// Sharp test photos read 9-12 labels and blurry ones none. OCR confidence is
// not used: the certificate's security pattern drags it down on good photos.
const MIN_LABELS = 5;
const MAX_SIDE = 1800;
const SCAN_TIMEOUT = 30000;

export function gradeGeranText({ text, confidence }) {
  const upperText = String(text || "").toUpperCase();
  const labels = GERAN_LABELS.filter((label) => upperText.includes(label));

  return {
    confidence: Number(confidence) || 0,
    labels: labels.length,
    readable: labels.length >= MIN_LABELS,
  };
}

// Phone photos are far larger than the OCR needs; shrinking them keeps the
// check to a few seconds on a mid-range handset.
async function shrinkImage(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");

    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    return canvas;
  } catch {
    return file;
  }
}

function rotateCanvas(source, degrees) {
  if (!degrees) return source;

  const canvas = document.createElement("canvas");
  const sideways = degrees % 180 !== 0;

  canvas.width = sideways ? source.height : source.width;
  canvas.height = sideways ? source.width : source.height;

  const context = canvas.getContext("2d");

  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((degrees * Math.PI) / 180);
  context.drawImage(source, -source.width / 2, -source.height / 2);

  return canvas;
}

// The OCR only reads upright text, and a geran is often photographed
// sideways. A geran is landscape, so a portrait photo is tried turned first.
function orientationsFor(image) {
  if (typeof image.getContext !== "function") return [0];

  return image.height > image.width ? [90, 270, 0, 180] : [0, 180, 90, 270];
}

// Resolves to "readable", "unreadable" or "skipped". The check only advises:
// a PDF, a slow connection or a failed OCR download never blocks the upload.
export async function scanGeran(file) {
  if (!file?.type?.startsWith("image/")) return { status: "skipped" };

  let worker;
  let timer;
  let abandoned = false;

  try {
    const scan = (async () => {
      const [{ createWorker }, image] = await Promise.all([
        import("tesseract.js"),
        shrinkImage(file),
      ]);

      worker = await createWorker("eng");

      let best = { readable: false, labels: -1 };

      for (const degrees of orientationsFor(image)) {
        // The timeout may already have given up on this scan.
        if (abandoned) break;

        const { data } = await worker.recognize(rotateCanvas(image, degrees));
        const grade = gradeGeranText(data);

        if (grade.labels > best.labels) best = grade;
        if (grade.readable) break;
      }

      return best;
    })();
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("scan timeout")), SCAN_TIMEOUT);
    });
    const grade = await Promise.race([scan, timeout]);

    return { ...grade, status: grade.readable ? "readable" : "unreadable" };
  } catch {
    abandoned = true;
    return { status: "skipped" };
  } finally {
    clearTimeout(timer);
    worker?.terminate?.();
  }
}
